// Espejo (frontend) del motor RBAC de backend/rbac.py.
// Sirve para ocultar acciones y cerrar la puerta de PII al super_admin en la UI.

// Recurso -> grupo funcional (mismo mapeo que backend GROUPS)
export const RESOURCE_GROUP = {
  clientes: "kyc", expedientes: "kyc", documentos: "kyc", beneficiarios: "kyc",
  prospectos: "credito", solicitudes: "credito", creditos: "credito",
  garantias: "credito", productos_credito: "credito",
  cobranza: "cobranza", pagos: "cobranza",
  matriz_riesgo: "pld", alertas: "pld", casos: "pld", reportes_cnbv: "pld",
  cedulas_riesgo: "pld", screening_matches: "pld", screening_runs: "pld",
  listas_internas: "pld", listas_internas_entradas: "pld",
  condusef_sipres: "condusef", condusef_reca: "condusef", condusef_reco: "condusef",
  condusef_reune: "condusef", condusef_redeco: "condusef",
  plantillas: "legal",
  regimenes: "normativa", obligaciones: "normativa", calendario: "normativa",
  actividades_vulnerables: "normativa", reportes: "normativa",
  integraciones: "integraciones",
  areas: "admin", suscripciones: "admin",
  entidades: "entidad",
  listas_globales: "listas_globales", listas_globales_entradas: "listas_globales",
};

// Colecciones con datos personales (super_admin => sin acceso)
export const PII_RESOURCES = new Set([
  "clientes", "expedientes", "documentos", "beneficiarios", "creditos", "pagos",
  "garantias", "cobranza", "alertas", "casos", "cedulas_riesgo", "operaciones",
  "prospectos", "solicitudes", "consultas_sic", "autorizaciones_sic", "reportes_sic",
  "condusef_reune", "condusef_redeco", "screening_matches", "screening_runs",
]);

// Colecciones de plataforma que el super_admin sí administra
export const PLATFORM_RESOURCES = new Set([
  "entidades", "suscripciones", "listas_globales", "listas_globales_entradas",
]);

// Rutas con PII (no /r/:slug) bloqueadas para super_admin
const PII_ROUTES = new Set([
  "/clientes-360", "/sofom/originacion", "/sofom/cartera",
  "/pld", "/condusef", "/centro-integraciones",
]);

export function isPiiRoute(pathname) {
  if (PII_ROUTES.has(pathname)) return true;
  const m = pathname.match(/^\/r\/([^/]+)/);
  if (m) return PII_RESOURCES.has(m[1]);
  return false;
}

// Módulos operativos de SOFOM ocultos al super_admin (plataforma pura).
// No son PII, pero no corresponden a la cuenta de administración del SaaS.
const SAAS_HIDDEN_ROUTES = new Set([
  "/sofom/simulador", "/calendario", "/documentos-legales",
  "/fiscal/catalogo", "/fiscal/config", "/fiscal/reportes",
]);
const SAAS_HIDDEN_R_SLUGS = new Set(["actividades_vulnerables", "reportes"]);

export function isSaasHiddenRoute(pathname) {
  if (SAAS_HIDDEN_ROUTES.has(pathname)) return true;
  const m = pathname.match(/^\/r\/([^/]+)/);
  if (m) return SAAS_HIDDEN_R_SLUGS.has(m[1]);
  return false;
}

// Estados de entidad que confinan al usuario al onboarding (sin acceso operativo).
export const ENTIDAD_BLOQUEADA = new Set(["pendiente_aprobacion", "rechazada", "rechazado_definitivo"]);
export function isEntidadBloqueada(estatus) {
  return ENTIDAD_BLOQUEADA.has(estatus);
}

// Etiquetas legibles de grupos y acciones (para la matriz de permisos)
export const GRUPO_LABEL = {
  kyc: "Clientes y expedientes (KYC)",
  credito: "Crédito y originación",
  cobranza: "Cobranza y pagos",
  pld: "PLD, riesgo y casos",
  condusef: "CONDUSEF",
  legal: "Documentos legales",
  normativa: "Normativa y reportes",
  integraciones: "Integraciones / Conexiones",
  admin: "Administración",
  entidad: "Datos de la entidad",
  listas_globales: "Listas globales (plataforma)",
};

export const ACCION_LABEL = {
  ver: "Ver", crear: "Crear", editar: "Editar", eliminar: "Eliminar", descargar: "Descargar",
};

export const ACCION_ORDER = ["ver", "crear", "editar", "eliminar", "descargar"];

// Roles no editables por nadie (reglas de segregación bloqueadas)
export const NON_EDITABLE_ROLES = new Set([
  "super_admin", "admin_entidad", "auditor_externo", "lectura",
]);

// Áreas del menú (8) y defaults por rol — espejo de backend rbac.MENU_AREAS / ROLE_DEFAULT_AREAS.
// Sirven para pre-marcar las casillas al asignar un rol (editable por el admin).
export const MENU_AREA_IDS = [
  "principal", "originacion", "leads_campo", "cartera", "cobranza",
  "clientes", "cumplimiento", "regulatorio", "administracion", "fiscal", "finanzas",
];

export const ROLE_AREA_DEFAULTS = {
  admin_entidad: "ALL",
  oficial_cumplimiento: ["clientes", "cumplimiento", "regulatorio", "originacion", "cartera", "cobranza"],
  analista_pld: ["clientes", "cumplimiento", "regulatorio"],
  ejecutivo_credito: ["originacion", "clientes", "cartera"],
  analista_credito: ["originacion", "clientes", "cartera"],
  comite_credito: ["originacion", "cartera"],
  cobranza: ["cobranza", "cartera", "clientes"],
  vendedor: ["leads_campo"],
  juridico: ["clientes", "regulatorio", "cumplimiento"],
  contabilidad: ["cartera", "regulatorio", "administracion", "finanzas"],
  contabilidad_fiscal: ["fiscal", "cobranza", "finanzas"],
  atencion_une: ["clientes", "cumplimiento"],
  tecnico: ["administracion"],
  auditor_externo: "ALL",
  lectura: ["principal"],
};

export function defaultAreasForRole(role) {
  const d = ROLE_AREA_DEFAULTS[role] || ["principal"];
  const base = d === "ALL" ? [...MENU_AREA_IDS] : [...d];
  if (!base.includes("principal")) base.unshift("principal");
  return base;
}

// ¿El usuario puede PROCESAR leads (convertir/descartar)?
// Regla: admin de la entidad, o tener AMBAS áreas EXPLÍCITAMENTE asignadas
// ("leads_campo" Y "originacion"). La auto-inclusión de la bandeja NO basta.
// Espejo del gate backend leads._puede_procesar (usa areas_acceso EXPLÍCITAS del usuario).
export function puedeProcesarLeads(user) {
  if (!user) return false;
  if (user.role === "admin_entidad") return true;
  if (user.role === "super_admin") return false;
  const a = user.areas_acceso || [];
  return a.includes("leads_campo") && a.includes("originacion");
}
